import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import { BRAND } from '../../config/brand';

// Tour steps differ between desktop and mobile because the desktop sidebar nav
// items (`nav-analysis`, `nav-history`, `nav-settings`) are hidden behind a
// hamburger on mobile. Pointing at them on mobile would silently fail (the
// spotlight target query returns null and the step never renders). Instead we
// surface the mobile menu toggle once, so users learn where everything else
// lives.
const DESKTOP_TOUR_STEPS = [
  { target: 'stats-grid', titleKey: 'onboarding.steps.stats.title', descKey: 'onboarding.steps.stats.description' },
  { target: 'quick-analysis', titleKey: 'onboarding.steps.analysis.title', descKey: 'onboarding.steps.analysis.description' },
  { target: 'quick-journal', titleKey: 'onboarding.steps.journal.title', descKey: 'onboarding.steps.journal.description' },
  { target: 'nav-analysis', titleKey: 'onboarding.steps.navAnalysis.title', descKey: 'onboarding.steps.navAnalysis.description' },
  { target: 'nav-history', titleKey: 'onboarding.steps.navHistory.title', descKey: 'onboarding.steps.navHistory.description' },
  { target: 'nav-settings', titleKey: 'onboarding.steps.navSettings.title', descKey: 'onboarding.steps.navSettings.description' },
];

const MOBILE_TOUR_STEPS = [
  { target: 'stats-grid', titleKey: 'onboarding.steps.stats.title', descKey: 'onboarding.steps.stats.description' },
  { target: 'quick-analysis', titleKey: 'onboarding.steps.analysis.title', descKey: 'onboarding.steps.analysis.description' },
  { target: 'quick-journal', titleKey: 'onboarding.steps.journal.title', descKey: 'onboarding.steps.journal.description' },
  { target: 'mobile-menu-toggle', titleKey: 'onboarding.steps.mobileMenu.title', descKey: 'onboarding.steps.mobileMenu.description' },
];

const MOBILE_BREAKPOINT_PX = 768;

function isMobile(): boolean {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < MOBILE_BREAKPOINT_PX;
}

interface OnboardingTourProps {
  onComplete: () => void;
}

interface SpotlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export default function OnboardingTour({ onComplete }: OnboardingTourProps) {
  const { t } = useTranslation('common');
  const [currentStep, setCurrentStep] = useState(0);
  const [rect, setRect] = useState<SpotlightRect | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<'below' | 'above'>('below');
  const tooltipRef = useRef<HTMLDivElement>(null);

  // Pick step set once on mount — the user isn't expected to resize across the
  // breakpoint mid-tour, and switching arrays mid-flight would reset progress.
  const [steps] = useState(() => (isMobile() ? MOBILE_TOUR_STEPS : DESKTOP_TOUR_STEPS));

  const step = steps[currentStep];

  const updatePosition = useCallback(() => {
    const el = document.querySelector(`[data-tour="${step.target}"]`);
    if (!el) return;

    // Instant (not smooth) so the getBoundingClientRect below measures the
    // final position — a smooth scroll is still mid-animation at the next
    // frame, which put the spotlight cutout off-screen on large/4K viewports
    // and dimmed the whole screen to black until a resize recomputed it.
    el.scrollIntoView({ block: 'center' });

    requestAnimationFrame(() => {
      const r = el.getBoundingClientRect();
      const padding = 8;
      setRect({
        top: r.top - padding,
        left: r.left - padding,
        width: r.width + padding * 2,
        height: r.height + padding * 2,
      });

      // Decide tooltip position: if element is in bottom half, show above
      const spaceBelow = window.innerHeight - r.bottom;
      setTooltipPosition(spaceBelow < 220 ? 'above' : 'below');
    });
  }, [step.target]);

  useEffect(() => {
    updatePosition();

    const handleResize = () => updatePosition();
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize, true);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize, true);
    };
  }, [updatePosition]);

  const handleComplete = useCallback(() => {
    localStorage.setItem(`${BRAND.storagePrefix}_onboarding_completed`, 'true');
    onComplete();
  }, [onComplete]);

  const handleNext = useCallback(() => {
    if (currentStep < steps.length - 1) {
      setCurrentStep((s) => s + 1);
    } else {
      handleComplete();
    }
  }, [currentStep, handleComplete, steps.length]);

  const handleBack = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep((s) => s - 1);
    }
  }, [currentStep]);

  const handleSkip = useCallback(() => {
    handleComplete();
  }, [handleComplete]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleSkip();
      if (e.key === 'ArrowRight' || e.key === 'Enter') handleNext();
      if (e.key === 'ArrowLeft') handleBack();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleSkip, handleNext, handleBack]);

  if (!rect) return null;

  const isLastStep = currentStep === steps.length - 1;
  const isFirstStep = currentStep === 0;

  // Calculate tooltip position
  const tooltipTop = tooltipPosition === 'below'
    ? rect.top + rect.height + 16
    : rect.top - 16;

  // Tooltip width: full viewport minus 16px gutter on mobile, 320px on >=md.
  // Centering math has to match the actual rendered width to keep the arrow
  // pointing at the spotlight target.
  const tooltipWidth = isMobile()
    ? Math.min(window.innerWidth - 32, 320)
    : 320;

  const tooltipLeft = Math.max(16, Math.min(
    rect.left + rect.width / 2 - tooltipWidth / 2,
    window.innerWidth - tooltipWidth - 16,
  ));

  // Arrow position relative to tooltip
  const arrowLeft = Math.max(24, Math.min(
    rect.left + rect.width / 2 - tooltipLeft,
    tooltipWidth - 16,
  ));

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key="tour-overlay"
        className="fixed inset-0 z-[9999] overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
      >
        {/* Backdrop with spotlight cutout */}
        <motion.div
          className="absolute rounded-lg"
          style={{
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
            boxShadow: '0 0 0 9999px rgba(0,0,0,0.7)',
            zIndex: 9999,
            pointerEvents: 'none',
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
          layoutId="spotlight"
        />

        {/* Clickable backdrop to prevent interaction */}
        <div
          className="absolute inset-0"
          style={{ zIndex: 9998 }}
          onClick={(e) => e.stopPropagation()}
        />

        {/* Tooltip */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            ref={tooltipRef}
            className="absolute bg-bg-elevated border border-border-default rounded-xl shadow-2xl p-5"
            style={{
              width: tooltipWidth,
              top: tooltipPosition === 'below' ? tooltipTop : undefined,
              bottom: tooltipPosition === 'above' ? window.innerHeight - tooltipTop : undefined,
              left: tooltipLeft,
              zIndex: 10000,
            }}
            initial={{ opacity: 0, y: tooltipPosition === 'below' ? -8 : 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: tooltipPosition === 'below' ? -8 : 8 }}
            transition={{ duration: 0.25 }}
          >
            {/* Arrow */}
            <div
              className="absolute w-3 h-3 bg-bg-elevated border-border-default rotate-45"
              style={{
                left: arrowLeft,
                ...(tooltipPosition === 'below'
                  ? { top: -6, borderTop: '1px solid', borderLeft: '1px solid', borderColor: 'inherit' }
                  : { bottom: -6, borderBottom: '1px solid', borderRight: '1px solid', borderColor: 'inherit' }),
              }}
            />

            {/* Step counter */}
            <p className="text-xs text-text-tertiary mb-2">
              {t('onboarding.stepOf', { current: currentStep + 1, total: steps.length })}
            </p>

            {/* Title */}
            <h3 className="text-base font-semibold text-text-primary mb-1">
              {t(step.titleKey)}
            </h3>

            {/* Description */}
            <p className="text-sm text-text-secondary mb-4 leading-relaxed">
              {t(step.descKey)}
            </p>

            {/* Progress dots */}
            <div className="flex items-center gap-1.5 mb-4">
              {steps.map((_, i) => (
                <div
                  key={i}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    i === currentStep
                      ? 'w-6 bg-accent-primary'
                      : i < currentStep
                        ? 'w-1.5 bg-accent-primary/50'
                        : 'w-1.5 bg-border-default'
                  }`}
                />
              ))}
            </div>

            {/* Buttons */}
            <div className="flex items-center justify-between">
              <button
                onClick={handleSkip}
                className="text-sm text-text-tertiary hover:text-text-secondary transition-colors"
              >
                {t('onboarding.skip')}
              </button>

              <div className="flex items-center gap-2">
                {!isFirstStep && (
                  <button
                    onClick={handleBack}
                    className="px-3 py-1.5 text-sm font-medium text-text-secondary hover:text-text-primary rounded-lg hover:bg-bg-tertiary transition-colors"
                  >
                    {t('onboarding.back')}
                  </button>
                )}
                <button
                  onClick={handleNext}
                  className="px-4 py-1.5 text-sm font-medium text-white bg-accent-primary hover:bg-accent-primary/90 rounded-lg transition-colors"
                >
                  {isLastStep ? t('onboarding.finish') : t('onboarding.next')}
                </button>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </motion.div>
    </AnimatePresence>
  );
}
